import Box from '@mui/material/Box';
import classNames from 'classnames';
import * as React from 'react';

import { useKeyboardEvent, useResponsive } from '../../hooks';
import { createContextFactory } from '../../context';
import MotionSlideIn from '../Animate/MotionSlideIn';
import { useDropdownContext } from '../Dropdown';
import ModalDiscard from './ModalDiscard';
import ModalFooter from './ModalFooter';
import ModalHeader from './ModalHeader';
import { useModalBackCloseRef } from './modalBackClose';
import { Portal } from '../Portal';

type ModalContextType = {
  setFooter: (footer: JSX.Element) => void;
  setHeader: (header: JSX.Element) => void;
};
const ModalContext = createContextFactory({
  devToolsName: 'ModalContext',
  initialValue: {} as ModalContextType,
});

export function useModalContext() {
  return ModalContext.useContext();
}

export interface ModalRefType {
  close: () => void;
  open: () => void;
}

export interface ModalClassesProps {
  overlay?: string;
  discard?: string;
  footer?: string;
  header?: string;
  root?: string;
  body?: string;
}
/**
 * Props for the Dialog component.
 */
export interface ModalProps {
  /**
   * Variant decides about modal width
   */
  variant?: 'full' | 'medium' | 'small';

  /**
   * Disables close when overlay is clicked.
   */
  disableOverlayClose?: boolean;

  /**
   * Ignores Escape only — the overlay, the close icon and browser back still
   * close. Escape is heard on `window`, so it also arrives when it was meant
   * for a menu or a dialog opened from inside this modal; a modal holding an
   * unsaved form sets this rather than lose the form to that Escape.
   */
  disableEscapeClose?: boolean;

  /**
   * Raises the overlay above other layered UI it may be opened from — a MUI
   * Dialog (zIndex.modal = 1300) or an open DropdownMenu (1350) — instead of
   * the default z-index. Kept below MUI's snackbar/tooltip layers (1400/1500).
   */
  elevated?: boolean;

  /**
   * On mobile, makes the sheet cover the full screen instead of appearing as
   * a bottom sheet with rounded corners.
   */
  mobileFullscreen?: boolean;

  classes?: ModalClassesProps;

  /**
   * The content of the dialog. Accepts ReactNode so callers can pass
   * conditional children (e.g. `{cond && <ModalFooter/>}`); non-element
   * children are ignored by the child-sorting effect below.
   */
  children?: React.ReactNode;
  /**
   * Removes close icon from the modal.
   */
  withoutCloseIcon?: boolean;
  /**
   * Indicates that modal should be opened on mount.
   */
  initiallyOpen?: boolean;
  /**
   * Callback function to be called when the dialog is closed.
   */
  onClose?: () => void;
  /**
   * Callback function to be called when the dialog is opened.
   */
  onOpen?: () => void;
  className?: string;
  /**
   * The title of the dialog.
   */
  title?: string;
  qa?: string;
}

interface ModalComponent extends React.ForwardRefExoticComponent<
  React.RefAttributes<ModalRefType> & Omit<ModalProps, 'ref'>
> {
  Footer: typeof ModalFooter;
  Header: typeof ModalHeader;
  displayName: string;
}

const Modal = React.forwardRef(
  (
    {
      children,
      className,
      classes,
      disableEscapeClose,
      disableOverlayClose,
      elevated = false,
      initiallyOpen,
      mobileFullscreen = false,
      onClose,
      onOpen,
      qa,
      variant = 'small',
      withoutCloseIcon,
    }: ModalProps,
    ref: React.ForwardedRef<ModalRefType>,
  ) => {
    // OPEN QUESTION, deliberately not decided here. Both of these used to read
    // `if (setDropdownOpen) setOpen(false); else setOpen(false);` — identical
    // arms, so `setDropdownOpen` was never called and a modal opened from
    // inside a Dropdown never closed the dropdown it came from. The arms are
    // collapsed to what actually ran; if closing SHOULD also close the parent
    // dropdown, that is a UX change and needs its own decision, not a silent
    // repair. (`useDropdownContext` is built on `{} as DropdownContextType`, so
    // outside a DropdownProvider its members really are `undefined` — a guard
    // here would be meaningful, which is why the old TS2774 was suppressed.)
    const { getFloatingProps, refs } = useDropdownContext();
    const isMobile = useResponsive('down', 'sm');
    const [footer, setFooter] = React.useState<JSX.Element | null>(null);
    const [header, setHeader] = React.useState<JSX.Element | null>(null);
    const [discardComponent, setDiscardComponent] =
      React.useState<JSX.Element | null>(null);
    const [otherChildren, setOtherChildren] = React.useState<JSX.Element[]>([]);
    const [open, setOpen] = React.useState<boolean>(initiallyOpen ?? false);
    const shouldTriggerDiscardRef = React.useRef<boolean>(false);
    const hasDiscardComponentRef = React.useRef<boolean>(false);
    const [discardModalOpen, setDiscardModalOpen] =
      React.useState<boolean>(false);

    const handleClose = React.useCallback(() => {
      /**
       * If there is discard component provided, it will first be triggered.
       * User can confirm choices or discard the action.
       */
      if (hasDiscardComponentRef.current && shouldTriggerDiscardRef.current) {
        shouldTriggerDiscardRef.current = false;
        setDiscardModalOpen(true);
        return;
      }
      if (onClose) onClose();
      setOpen(false);
    }, [onClose, setDiscardModalOpen]);

    const handleOpen = React.useCallback(() => {
      setOpen(true);
      if (onOpen) onOpen();
    }, [onOpen]);

    // Closes modal on Escape press
    useKeyboardEvent('Escape', () => {
      if (!disableOverlayClose && !disableEscapeClose) handleClose();
    });

    // Browser back closes this modal alone — see MODAL_BACK_CLOSE_EVENT
    const backCloseOverlayRef = useModalBackCloseRef(() => {
      if (!disableOverlayClose) handleClose();
    });

    const handleDiscardClose = React.useCallback(() => {
      setDiscardModalOpen(false);
      shouldTriggerDiscardRef.current = false;
      handleClose();
    }, [handleClose]);

    const handleDiscardConfirm = React.useCallback(() => {
      setDiscardModalOpen(false);
      shouldTriggerDiscardRef.current = false;
      handleClose();
    }, [handleClose]);

    React.useEffect(() => {
      let footer: JSX.Element | null = null;
      let header: JSX.Element | null = null;
      let discard: JSX.Element | null = null;
      const other: JSX.Element[] = [];
      React.Children.forEach(children, (child) => {
        // Skips falsy/non-element children (e.g. a `false` from `{cond && …}`)
        // and narrows `child` to a ReactElement so `.type`/`.props` are typed.
        if (!React.isValidElement(child)) return;
        switch (child.type) {
          case ModalFooter:
            footer = React.cloneElement(child, {
              ...child?.props,
              onClose: handleClose,
              qa,
            });
            break;
          case ModalHeader:
            header = React.cloneElement(child, {
              ...child?.props,
              onClose: handleClose,
              qa,
              withoutCloseIcon,
            });
            break;
          case ModalDiscard:
            discard = React.cloneElement(child, {
              ...child?.props,
              onClose: handleDiscardClose,
              onConfirm: handleDiscardConfirm,
              open: discardModalOpen,
              qa,
            });
            hasDiscardComponentRef.current = true;
            shouldTriggerDiscardRef.current = true;
            break;
          default:
            other.push(child);
        }
      });
      setFooter(footer);
      setHeader(header);
      setDiscardComponent(discard);
      setOtherChildren(other);
    }, [
      children,
      discardModalOpen,
      withoutCloseIcon,
      handleClose,
      qa,
      handleDiscardClose,
      handleDiscardConfirm,
    ]);

    React.useImperativeHandle(ref, () => {
      return {
        close: handleClose,
        open: handleOpen,
      };
    }, [handleClose, handleOpen]);
    React.useEffect(() => {
      if (initiallyOpen) {
        handleOpen();
      }
    }, [initiallyOpen, handleClose, handleOpen]);

    const floatingProps = getFloatingProps ? getFloatingProps() : {};

    const providedValues = React.useMemo(() => {
      return { setFooter, setHeader };
    }, [setHeader, setFooter]);

    if (!open) return null;
    return (
      <ModalContext.Provider value={providedValues}>
        {discardComponent}
        <Portal {...floatingProps}>
          <Box
            className={classNames(
              // Kept as a plain marker (no CSS of its own) so
              // Autocomplete.scss's `.Mfw-Modal-overlay` ancestor selector
              // still matches.
              'Mfw-Modal-overlay',
              classes?.overlay,
              className,
            )}
            ref={(e: HTMLElement | null) => {
              backCloseOverlayRef(e);
              if (refs && refs.setFloating) refs.setFloating(e);
            }}
            onClick={() => {
              if (!disableOverlayClose) handleClose();
            }}
            sx={{
              position: 'fixed',
              inset: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: isMobile ? 'flex-end' : 'center',
              justifyContent: 'center',
              zIndex: elevated ? 1360 : 20,
              bgcolor: 'rgba(0, 0, 0, 0.1)',
              backdropFilter: 'blur(2px)',
              ...(isMobile && mobileFullscreen && { alignItems: 'stretch' }),
            }}
          >
            <MotionSlideIn
              style={{
                height: '100%',
                width: '100%',
                display: 'flex',
                alignItems: isMobile ? 'flex-end' : 'center',
                justifyContent: 'center',
                ...(isMobile && mobileFullscreen && { alignItems: 'stretch' }),
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <Box
                className={classes?.root}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  height: '100%',
                  maxHeight: '640px',
                  borderRadius: '16px',
                  bgcolor: 'background.paper',
                  ...(variant === 'small' && { maxWidth: 440 }),
                  ...(variant === 'medium' && { maxWidth: 950 }),
                  ...(variant === 'full' &&
                    !isMobile && {
                      maxWidth: '90vw',
                      maxHeight:
                        'calc(var(--mfw-window-height, 100dvh) * 0.95)',
                    }),
                  ...(isMobile && {
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                    pb: 2,
                    maxHeight: 'calc(var(--mfw-window-height, 100dvh) * 0.95)',
                    minHeight: '32%',
                  }),
                  ...(isMobile &&
                    mobileFullscreen && {
                      borderRadius: 0,
                      maxHeight: 'var(--mfw-window-height, 100dvh)',
                      minHeight: '100%',
                    }),
                }}
              >
                <Box
                  className={classes?.header}
                  sx={{
                    ...(isMobile && { px: 2 }),
                    '& .MuiTypography-root.Mfw-DropdownMenu-title': {
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 2,
                      textOverflow: 'ellipsis',
                    },
                  }}
                >
                  {header}
                </Box>
                <Box
                  className={classes?.body}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    overflowY: 'auto',
                    px: 2,
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none',
                  }}
                >
                  {otherChildren}
                </Box>
                {footer && (
                  <Box
                    className={classes?.footer}
                    sx={{
                      px: isMobile ? 1 : 2,
                      pb: isMobile ? 0 : 2,
                      pt: isMobile ? 1 : 2,
                    }}
                  >
                    {footer}
                  </Box>
                )}
              </Box>
            </MotionSlideIn>
          </Box>
        </Portal>
      </ModalContext.Provider>
    );
  },
) as ModalComponent;

Modal.displayName = 'Modal';
Modal.Footer = ModalFooter;
Modal.Header = ModalHeader;

export default Modal;
